import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';
import { getMemberImage, getMemberImagePosition, getMemberImageScale } from '../data/teamMemberImages';

const Teams = () => {
  const { t } = useLanguage();
  usePageTitle('Teams');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="overflow-x-hidden">
      <section className="relative pt-28 pb-12 sm:pt-36 sm:pb-20 overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight mb-2">
            {t('home.teamsTitle')}
          </h1>
          {t('home.teamsSubtitle') && (
            <p className="text-gray-400 text-sm sm:text-base max-w-2xl mb-10 leading-relaxed">
              {t('home.teamsSubtitle')}
            </p>
          )}

          {/* Board */}
          <div className="mb-8 md:mb-10">
            <h2 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">
              {t('home.teams.board')}
            </h2>
            <div className="border border-dark-border bg-dark-card rounded-xl p-4 sm:p-5 flex flex-wrap items-center gap-3 sm:gap-4">
              {['boardMember0', 'boardMember1', 'boardMember2', 'boardMember3', 'boardMember4', 'boardMember5'].map((key) => {
                const name = t(`home.teams.${key}.name`);
                const img = getMemberImage(name);
                return (
                  <div key={key} className="flex items-center gap-3 min-w-0">
                    <div className="w-12 h-12 rounded-full bg-dark-surface border border-dark-border flex items-center justify-center text-gray-500 text-sm font-medium flex-shrink-0 overflow-hidden">
                      {img ? (
                        <img src={img} alt={name} loading="lazy" className="w-full h-full object-cover" style={{ objectPosition: getMemberImagePosition(name), ...(getMemberImageScale(name) && { transform: getMemberImageScale(name) }) }} />
                      ) : (
                        '?'
                      )}
                    </div>
                    <div>
                      <div className="text-white font-medium text-sm">{name}</div>
                      <div className="text-gray-500 text-xs">{t(`home.teams.${key}.role`)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Advisors / Mentors Team */}
          {(() => {
            const key = 'mentorsTeam';
            const members = t('home.teamMembers')?.[key] || [];
            return (
              <div className="mb-8 md:mb-10">
                <h2 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">
                  {t('home.teams.mentorsTeam')}
                </h2>
                <div className="border border-dark-border bg-dark-card rounded-xl p-4 sm:p-5 flex flex-wrap items-center gap-3 sm:gap-4 hover:border-dark-border-light transition-colors duration-300">
                  {Array.isArray(members) && members.length > 0 ? (
                    members.map((member, i) => {
                      const img = getMemberImage(member.name);
                      return (
                        <div key={i} className="flex items-center gap-3 min-w-0">
                          <div className="w-12 h-12 rounded-full bg-dark-surface border border-dark-border flex items-center justify-center text-gray-500 text-sm font-medium flex-shrink-0 overflow-hidden">
                            {img ? (
                              <img src={img} alt={member.name} loading="lazy" className="w-full h-full object-cover" style={{ objectPosition: getMemberImagePosition(member.name), ...(getMemberImageScale(member.name) && { transform: getMemberImageScale(member.name) }) }} />
                            ) : (
                              '?'
                            )}
                          </div>
                          <div>
                            <div className="text-white font-medium text-sm">{member.name}</div>
                            <div className="text-gray-500 text-xs">{member.role}</div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-12 h-12 rounded-full bg-dark-surface border border-dark-border flex items-center justify-center text-gray-500 text-sm font-medium flex-shrink-0">
                          ?
                        </div>
                        <div>
                          <div className="text-white font-medium text-sm">Placeholder</div>
                          <div className="text-gray-500 text-xs">Role</div>
                        </div>
                      </div>
                      <div className="text-gray-500 text-xs sm:text-sm">+ more</div>
                    </>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Teams grid */}
          <div className="grid grid-cols-1 gap-4 sm:gap-6">
            {[
              'aiSoftwareEngineerTeam',
              'securityTeam',
              'productTeam',
              'dataTeam',
              'marketingTeam',
              'legalTeam',
              'uiuxTeam',
              'hrTeam',
            ].map((key) => {
              const members = t('home.teamMembers')?.[key] || [];
              return (
                <div key={key}>
                  <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3">
                    {t(`home.teams.${key}`)}
                  </h3>
                  <div className="border border-dark-border bg-dark-card rounded-xl p-4 sm:p-5 flex flex-wrap items-center gap-3 sm:gap-4 hover:border-dark-border-light transition-colors duration-300">
                    {Array.isArray(members) && members.length > 0 ? (
                      members.map((member, i) => {
                        const img = getMemberImage(member.name);
                        return (
                          <div key={i} className="flex items-center gap-3 min-w-0">
                            <div className="w-12 h-12 rounded-full bg-dark-surface border border-dark-border flex items-center justify-center text-gray-500 text-sm font-medium flex-shrink-0 overflow-hidden">
                              {img ? (
                                <img src={img} alt={member.name} loading="lazy" className="w-full h-full object-cover" style={{ objectPosition: getMemberImagePosition(member.name), ...(getMemberImageScale(member.name) && { transform: getMemberImageScale(member.name) }) }} />
                              ) : (
                                '?'
                              )}
                            </div>
                            <div>
                              <div className="text-white font-medium text-sm">{member.name}</div>
                              <div className="text-gray-500 text-xs">{member.role}</div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-12 h-12 rounded-full bg-dark-surface border border-dark-border flex items-center justify-center text-gray-500 text-sm font-medium flex-shrink-0">
                            ?
                          </div>
                          <div>
                            <div className="text-white font-medium text-sm">Placeholder</div>
                            <div className="text-gray-500 text-xs">Role</div>
                          </div>
                        </div>
                        <div className="text-gray-500 text-xs sm:text-sm">+ more</div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-10">
            <Link
              to="/careers"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-accent font-medium text-sm rounded-full border border-accent/30 hover:bg-accent/10 transition-all duration-300"
            >
              {t('footer.about.join')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Teams;
